import { Box } from '@mui/material';
import type { ReactElement } from 'react';

import { useTokens, type SeriesToken } from './seriesPalette';

/**
 * Legende d'un graphique (maquette « Légende » : pastille, libelle, valeur).
 *
 * Elle est le second repere des series, apres la couleur : meme ordre que les
 * series, un motif de trait pour les courbes, le libelle ecrit en toutes
 * lettres. Elle est **masquee aux lecteurs d'ecran** : la table du cadre
 * porte deja chaque libelle et chaque valeur, la lire deux fois n'aiderait pas.
 */
export interface LegendItem {
  readonly label: string;
  readonly token: SeriesToken;
  /** Valeur ecrite a droite, telle que l'appelant la fournit (« 41 % »). */
  readonly value?: string;
  /** Motif de trait de la courbe (`stroke-dasharray`), pour `shape: 'line'`. */
  readonly dash?: string | undefined;
  readonly shape?: 'line' | 'square';
}

export interface LegendProps {
  readonly items: readonly LegendItem[];
  readonly direction?: 'row' | 'column';
}

export const Legend = ({ items, direction = 'row' }: LegendProps): ReactElement => {
  const tokens = useTokens();

  return (
    <Box
      component="ul"
      aria-hidden
      sx={{
        listStyle: 'none',
        m: 0,
        p: 0,
        display: 'flex',
        flexDirection: direction,
        flexWrap: 'wrap',
        gap: direction === 'row' ? '6px 18px' : '10px',
        fontSize: 13,
        minWidth: 0,
      }}
    >
      {items.map((item, rank) => (
        <Box key={rank} component="li" sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <svg width={item.shape === 'line' ? 20 : 10} height={10} focusable="false" style={{ flex: 'none' }}>
            {item.shape === 'line' ? (
              <line
                x1={1}
                x2={19}
                y1={5}
                y2={5}
                stroke={tokens[item.token]}
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeDasharray={item.dash}
              />
            ) : (
              <rect width={10} height={10} rx={3} fill={tokens[item.token]} />
            )}
          </svg>
          <span>{item.label}</span>
          {item.value === undefined ? null : (
            <Box component="span" sx={{ ml: 'auto', pl: 1, fontWeight: 700 }}>
              {item.value}
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
};
